import React, { useCallback, useEffect, useMemo, useState } from 'react';
import MainLayout from '../../layouts/MainLayout';
import {
  Card,
  Table,
  Button,
  Space,
  Tag,
  message,
  Modal,
  Form,
  Select,
  AutoComplete,
  Switch,
  Avatar,
  Spin,
  Alert,
  Popconfirm,
  Progress,
  Tooltip,
} from 'antd';
import {
  TeamOutlined,
  PlusOutlined,
  ReloadOutlined,
  DeleteOutlined,
  PhoneOutlined,
  EditOutlined,
} from '@ant-design/icons';
import { callCenterAgentsApi, callCenterQueuesApi, show422Error, ValidationError } from '../../utils/api';
import { getImagePath } from '../../utils/imageHelper';
import { TableSkeleton } from '../../components/SkeletonLoaders';
import PermissionGate from '../../components/PermissionGate';
import { PANEL_PRIMARY_BTN_CLASS } from '../../constants/ui';
import { bandMeta, describeDelta } from '../../components/call-center/operatorScore';

const CallCenterAgents = () => {
  const [loading, setLoading] = useState(true);
  const [agents, setAgents] = useState([]);
  const [addOpen, setAddOpen] = useState(false);
  const [editingAgent, setEditingAgent] = useState(null);
  const [addForm] = Form.useForm();
  const [loadingCreateMeta, setLoadingCreateMeta] = useState(false);
  const [createEmployees, setCreateEmployees] = useState([]);
  const [extensions, setExtensions] = useState([]);
  const [queues, setQueues] = useState([]);
  const [asteriskSynced, setAsteriskSynced] = useState(true);
  const [asteriskMessage, setAsteriskMessage] = useState(null);
  const [addSubmitting, setAddSubmitting] = useState(false);
  const [deleteLoadingId, setDeleteLoadingId] = useState(null);
  const [queueLoadingId, setQueueLoadingId] = useState(null);

  const loadAgents = useCallback(async () => {
    setLoading(true);
    try {
      const res = await callCenterAgentsApi.getAll();
      setAgents(res?.data ?? []);
    } catch (error) {
      console.error('خطا در دریافت اپراتورها:', error);
      message.error('خطا در دریافت لیست اپراتورها');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadQueues = useCallback(async () => {
    try {
      const res = await callCenterQueuesApi.getAll();
      const payload = res?.data ?? [];
      setQueues(Array.isArray(payload) ? payload : payload.data ?? []);
    } catch (error) {
      console.error('خطا در دریافت صف‌ها:', error);
    }
  }, []);

  useEffect(() => {
    loadAgents();
    loadQueues();
  }, [loadAgents, loadQueues]);

  // یک کاربر می‌تواند بیش از یک داخلی داشته باشد؛ بنابراین کارمندان قبلی فیلتر نمی‌شوند
  const availableEmployees = useMemo(
    () => createEmployees,
    [createEmployees],
  );

  const availableExtensions = useMemo(
    () => extensions.filter((ext) => !ext.assigned_user_id || (editingAgent && String(ext.extension) === String(editingAgent.extension))),
    [extensions, editingAgent],
  );

  const openAddModal = async () => {
    setEditingAgent(null);
    setAddOpen(true);
    addForm.resetFields();
    addForm.setFieldsValue({ is_active: true });
    setLoadingCreateMeta(true);
    try {
      const res = await callCenterAgentsApi.getCreateForm();
      const data = res?.data ?? {};
      setCreateEmployees(data.employees ?? []);
      setExtensions(data.extensions ?? []);
      setQueues(data.queues ?? []);
      setAsteriskSynced(data.asterisk_synced !== false);
      setAsteriskMessage(data.asterisk_message ?? null);
    } catch (error) {
      console.error('خطا در دریافت فرم اپراتور:', error);
      message.error('خطا در دریافت اطلاعات فرم');
      setAddOpen(false);
    } finally {
      setLoadingCreateMeta(false);
    }
  };

  const openEditModal = async (agent) => {
    setEditingAgent(agent);
    setAddOpen(true);
    addForm.resetFields();
    addForm.setFieldsValue({
      user_id: agent.user_id,
      extension: agent.extension,
      queue_id: agent.queue_id || undefined,
      is_active: agent.is_active !== false,
    });
    setLoadingCreateMeta(true);
    try {
      const res = await callCenterAgentsApi.getCreateForm();
      const data = res?.data ?? {};
      setCreateEmployees(data.employees ?? []);
      setExtensions(data.extensions ?? []);
      setQueues(data.queues ?? []);
      setAsteriskSynced(data.asterisk_synced !== false);
      setAsteriskMessage(data.asterisk_message ?? null);
    } catch (error) {
      console.error('خطا در دریافت فرم اپراتور:', error);
      message.error('خطا در دریافت اطلاعات فرم');
    } finally {
      setLoadingCreateMeta(false);
    }
  };

  const submitAddAgent = async () => {
    try {
      const values = await addForm.validateFields();
      setAddSubmitting(true);
      const payload = {
        user_id: values.user_id,
        extension: values.extension,
        queue_id: values.queue_id || null,
        is_active: values.is_active,
      };

      if (editingAgent) {
        await callCenterAgentsApi.update(editingAgent.id, payload);
        message.success('اپراتور با موفقیت بروزرسانی شد');
      } else {
        await callCenterAgentsApi.create(payload);
        message.success('اپراتور با موفقیت ثبت شد');
      }

      setAddOpen(false);
      setEditingAgent(null);
      await loadAgents();
    } catch (error) {
      if (error?.errorFields) return;
      console.error('خطا در ثبت اپراتور:', error);
      if (error instanceof ValidationError) {
        show422Error(error, addForm);
      } else {
        message.error(error?.message || 'خطا در ثبت اپراتور');
      }
    } finally {
      setAddSubmitting(false);
    }
  };

  const deleteAgent = async (row) => {
    setDeleteLoadingId(row.id);
    try {
      await callCenterAgentsApi.remove(row.id);
      message.success('اپراتور حذف شد');
      await loadAgents();
    } catch (error) {
      console.error('خطا در حذف اپراتور:', error);
      message.error(error?.message || 'خطا در حذف اپراتور');
    } finally {
      setDeleteLoadingId(null);
    }
  };

  const handleQueueChange = async (row, newQueueId) => {
    if (row.queue_id === newQueueId) return;
    setQueueLoadingId(row.id);
    try {
      await callCenterAgentsApi.update(row.id, {
        queue_id: newQueueId || null,
      });
      const selectedQueue = (queues || []).find((q) => q.id === newQueueId);
      if (selectedQueue) {
        message.success(`صف اپراتور به «${selectedQueue.title || selectedQueue.name}» تغییر کرد`);
      } else {
        message.success(`صف از اپراتور برداشته شد`);
      }
      setAgents((prev) =>
        prev.map((a) =>
          a.id === row.id
            ? { ...a, queue_id: newQueueId || null, queue: selectedQueue || null }
            : a
        )
      );
    } catch (error) {
      console.error('خطا در تغییر صف اپراتور:', error);
      message.error(error?.message || 'خطا در تغییر صف اپراتور');
    } finally {
      setQueueLoadingId(null);
    }
  };

  const columns = [
    {
      title: 'اپراتور',
      key: 'user',
      render: (_, row) => {
        const user = row.user || {};
        return (
          <Space>
            <Avatar src={getImagePath(user.image_url || user.image)} size={40}>
              {(user.name || '?').charAt(0)}
            </Avatar>
            <div>
              <div className="custom-font font-medium">{user.name || '—'}</div>
              <div className="custom-font text-xs text-gray-500">{user.email || ''}</div>
            </div>
          </Space>
        );
      },
    },
    {
      title: 'داخلی',
      dataIndex: 'extension',
      key: 'extension',
      width: 100,
      render: (ext) => (
        <Tag icon={<PhoneOutlined />} className="custom-font" dir="ltr">
          {ext || '—'}
        </Tag>
      ),
    },
    {
      title: 'صف تماس (Queue)',
      key: 'queue',
      width: 220,
      render: (_, record) => (
        <PermissionGate
          permission="manage_callcenter_setting"
          fallback={
            record.queue ? (
              <Tag color="blue" className="custom-font">
                {record.queue.title && record.queue.title !== record.queue.name
                  ? `${record.queue.title} (${record.queue.name})`
                  : (record.queue.title || record.queue.name)}
              </Tag>
            ) : (
              <span className="custom-font text-gray-400">—</span>
            )
          }
        >
          <Select
            size="small"
            placeholder="انتخاب یا تغییر صف..."
            className="custom-font w-full"
            popupClassName="custom-font"
            allowClear
            showSearch
            loading={queueLoadingId === record.id}
            disabled={queueLoadingId === record.id}
            value={record.queue_id || undefined}
            onChange={(val) => handleQueueChange(record, val)}
            filterOption={(input, option) =>
              (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
            }
            options={(queues || []).map((q) => {
              const label = q.title && q.title !== q.name ? `${q.title} (${q.name})` : (q.title || q.name);
              return {
                value: q.id,
                label,
              };
            })}
          />
        </PermissionGate>
      ),
    },
    {
      title: (
        <Tooltip title="میانگین امتیاز عملکرد (۰ تا ۱۰۰) تماس‌های تحلیل‌شده در ۳۰ روز اخیر؛ برای بهبود عملکرد و رقابت سالم، نه تنبیه">
          <span className="custom-font">امتیاز عملکرد (۳۰ روز)</span>
        </Tooltip>
      ),
      key: 'operator_score',
      width: 210,
      sorter: (a, b) => (a.operator_score_stats?.avg ?? -1) - (b.operator_score_stats?.avg ?? -1),
      render: (_, row) => {
        const stats = row.operator_score_stats;
        if (!stats || stats.avg == null) {
          return <span className="custom-font text-xs text-gray-400">بدون تماس امتیازدار</span>;
        }
        const band = bandMeta(Math.round(stats.avg));
        const delta = describeDelta(stats.delta, 'میانگین تیم');
        return (
          <div className="flex items-center gap-3">
            <Progress
              type="circle"
              size={46}
              strokeWidth={10}
              percent={stats.avg}
              strokeColor={{ '0%': band.from, '100%': band.to }}
              trailColor="rgba(148,163,184,0.2)"
              format={() => <span className={`custom-font text-xs font-bold ${band.text}`}>{Math.round(stats.avg)}</span>}
            />
            <div className="min-w-0">
              <div className={`custom-font text-xs font-bold ${band.text}`}>{band.label}</div>
              <div className="custom-font text-[11px] text-gray-400">
                {Number(stats.count).toLocaleString('fa-IR')} تماس
                {stats.min != null && <span dir="ltr"> · {stats.min}–{stats.max}</span>}
              </div>
              {delta && (
                <div className={`custom-font text-[11px] ${delta.tone === 'up' ? 'text-emerald-600' : delta.tone === 'down' ? 'text-red-600' : 'text-gray-400'}`}>
                  {delta.tone === 'up' ? '▲' : delta.tone === 'down' ? '▼' : '●'} {delta.text}
                </div>
              )}
            </div>
          </div>
        );
      },
    },
    {
      title: 'وضعیت',
      dataIndex: 'is_active',
      key: 'is_active',
      width: 90,
      render: (active) => (
        <Tag color={active ? 'green' : 'default'} className="custom-font">
          {active ? 'فعال' : 'غیرفعال'}
        </Tag>
      ),
    },
    {
      title: 'عملیات',
      key: 'actions',
      width: 120,
      render: (_, row) => (
        <PermissionGate
          permission="manage_callcenter_setting"
          fallback={<span className="text-gray-400 text-sm">—</span>}
        >
          <Space>
            <Tooltip title="ویرایش">
              <Button
                type="link"
                size="small"
                icon={<EditOutlined />}
                onClick={() => openEditModal(row)}
              />
            </Tooltip>
            <Popconfirm
              title="حذف این اپراتور؟"
              description="داخلی از این کارمند جدا می‌شود."
              okText="بله"
              cancelText="خیر"
              onConfirm={() => deleteAgent(row)}
            >
              <Button
                type="link"
                danger
                size="small"
                icon={<DeleteOutlined />}
                loading={deleteLoadingId === row.id}
              />
            </Popconfirm>
          </Space>
        </PermissionGate>
      ),
    },
  ];

  return (
    <MainLayout>
      <div style={{ fontFamily: 'CustomFont' }}>
        <Card className="shadow-xl border-0" bodyStyle={{ padding: 0 }}>
          <div className="flex items-center justify-between flex-wrap gap-4 p-6 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 rounded-t-xl">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-teal-500 to-teal-600 text-white flex items-center justify-center shadow-lg">
                <TeamOutlined className="text-xl" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100 m-0">
                  اپراتورها
                </h1>
                <p className="text-gray-500 dark:text-gray-400 text-sm m-0">
                  مدیریت اپراتورهای مرکز تماس و تخصیص داخلی سانترال (Asterisk)
                </p>
              </div>
            </div>
            <Space wrap>
              <Button icon={<ReloadOutlined />} onClick={loadAgents} loading={loading}>
                بروزرسانی
              </Button>
              <PermissionGate permission="manage_callcenter_setting">
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={openAddModal}
                  className={PANEL_PRIMARY_BTN_CLASS}
                >
                  افزودن اپراتور
                </Button>
              </PermissionGate>
            </Space>
          </div>

          <div className="p-6 bg-gray-50 dark:bg-gray-900/50 rounded-b-xl">
            {loading ? (
              <TableSkeleton rows={5} columns={6} />
            ) : (
              <Table
                rowKey="id"
                columns={columns}
                dataSource={agents}
                pagination={{ pageSize: 10, showSizeChanger: true, pageSizeOptions: ['10', '20', '50'] }}
                locale={{ emptyText: 'اپراتوری ثبت نشده است' }}
              />
            )}
          </div>
        </Card>

        <Modal
          title={editingAgent ? 'ویرایش اپراتور' : 'افزودن اپراتور'}
          open={addOpen}
          onCancel={() => {
            setAddOpen(false);
            setEditingAgent(null);
          }}
          onOk={submitAddAgent}
          confirmLoading={addSubmitting}
          destroyOnClose
          okText={editingAgent ? 'ذخیره' : 'ثبت'}
          cancelText="انصراف"
          width={520}
          okButtonProps={{ className: PANEL_PRIMARY_BTN_CLASS }}
        >
          <Spin spinning={loadingCreateMeta}>
            {!asteriskSynced && asteriskMessage && (
              <Alert
                type="info"
                showIcon
                className="mb-4 custom-font"
                message={asteriskMessage}
              />
            )}
            <Form form={addForm} layout="vertical" className="custom-font" style={{ marginTop: 8 }}>
              <Form.Item
                name="user_id"
                label="کارمند"
                rules={[{ required: true, message: 'کارمند را انتخاب کنید' }]}
              >
                <Select
                  showSearch
                  optionFilterProp="label"
                  placeholder="انتخاب کارمند"
                  popupClassName="custom-font"
                  className="custom-font"
                  options={availableEmployees.map((e) => {
                    const currentExts =
                      e.assigned_extensions?.length > 0
                        ? e.assigned_extensions
                        : (e.agent_extensions || []).map((x) => x.extension).filter(Boolean);
                    const extLabel = currentExts.length > 0 ? ` (داخلی‌های فعلی: ${currentExts.join(', ')})` : '';
                    return {
                      value: e.id,
                      label: `${e.name} — ${e.email}${extLabel}`,
                    };
                  })}
                  notFoundContent={
                    <span className="custom-font text-gray-500">کارمندی یافت نشد</span>
                  }
                />
              </Form.Item>

              <Form.Item
                name="extension"
                label="داخلی سانترال / استریسک"
                rules={[{ required: true, message: 'داخلی را وارد یا انتخاب کنید' }]}
                extra="می‌توانید داخلی را از لیست انتخاب کرده یا شماره جدید تایپ کنید (مثال: 101)"
              >
                <AutoComplete
                  placeholder="مثال: 101 یا انتخاب از لیست"
                  className="custom-font w-full"
                  popupClassName="custom-font"
                  options={availableExtensions.map((ext) => ({
                    value: String(ext.extension),
                    label: `${ext.extension}${ext.name && ext.name !== ext.extension ? ` — ${ext.name}` : ''}${ext.channel ? ` (${ext.channel})` : ''}`,
                  }))}
                  filterOption={(inputValue, option) =>
                    String(option?.value || '').toLowerCase().includes(inputValue.toLowerCase()) ||
                    String(option?.label || '').toLowerCase().includes(inputValue.toLowerCase())
                  }
                />
              </Form.Item>

              <Form.Item
                name="queue_id"
                label="صف تماس (Queue)"
                rules={[{ required: false }]}
              >
                <Select
                  placeholder="انتخاب صف..."
                  className="custom-font w-full"
                  popupClassName="custom-font"
                  showSearch
                  allowClear
                  filterOption={(input, option) =>
                    (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                  }
                  options={(queues || []).map((q) => {
                    const label = q.title && q.title !== q.name ? `${q.title} (${q.name})` : (q.title || q.name);
                    return {
                      value: q.id,
                      label,
                    };
                  })}
                />
              </Form.Item>

              <Form.Item
                name="is_active"
                label="فعال"
                valuePropName="checked"
              >
                <Switch checkedChildren="فعال" unCheckedChildren="غیرفعال" />
              </Form.Item>
            </Form>
          </Spin>
        </Modal>
      </div>
    </MainLayout>
  );
};

export default CallCenterAgents;
